import { useParams, Link } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { radioStations, type RadioStation } from '@/mocks/radios';
import { getStationLives, type RadioLive, type LiveComment, type LiveReaction } from '@/mocks/radioContent';
import { SwalAlert } from '@/hooks/useSweetAlert';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import ReportButton from '@/components/feature/ReportButton';

type PageState = 'loading' | 'ready' | 'empty' | 'error';

function StatusBadge({ status }: { status: RadioLive['status'] }) {
  if (status === 'en_vivo') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-accent-500 rounded-full">
        <span className="w-2 h-2 bg-background-50 rounded-full animate-pulse"></span>
        <span className="text-[10px] font-bold text-background-50 dark:text-foreground-950 uppercase tracking-wider whitespace-nowrap">EN VIVO</span>
      </span>
    );
  }
  if (status === 'programado') {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-secondary-500 rounded-full">
        <i className="ri-time-line text-[10px] text-background-50 dark:text-foreground-950"></i>
        <span className="text-[10px] font-bold text-background-50 dark:text-foreground-950 uppercase tracking-wider whitespace-nowrap">PROGRAMADO</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-foreground-200/80 rounded-full">
      <i className="ri-check-double-line text-[10px] text-foreground-600"></i>
      <span className="text-[10px] font-bold text-foreground-600 uppercase tracking-wider whitespace-nowrap">FINALIZADO</span>
    </span>
  );
}

function FormattedCount({ count }: { count: number }) {
  if (count >= 1000000) return <span>{(count / 1000000).toFixed(1)}M</span>;
  if (count >= 1000) return <span>{(count / 1000).toFixed(1)}K</span>;
  return <span>{count}</span>;
}

function LiveChat({ live, onSendMessage, messages, onReaction, onReport }: {
  live: RadioLive;
  onSendMessage: (text: string) => void;
  messages: LiveComment[];
  onReaction: (type: string) => void;
  onReport: (commentId: number) => void;
}) {
  const [input, setInput] = useState('');
  const reactions = live.reactions || [];
  const allMessages = [...(live.comments || []), ...messages];

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed) {
      SwalAlert.warning('Mensaje vacío', 'Escribí algo para enviar.');
      return;
    }
    onSendMessage(trimmed);
    setInput('');
  };

  return (
    <div className="bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden flex flex-col h-full">
      <div className="px-5 py-4 border-b border-background-200/70 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <i className="ri-chat-3-line text-lg text-foreground-500"></i>
          <span className="text-base font-semibold text-foreground-950">Chat en vivo</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 bg-accent-500 rounded-full animate-pulse"></span>
          <span className="text-sm text-foreground-500 font-medium"><FormattedCount count={live.viewers} /> viendo</span>
        </div>
      </div>

      {reactions.length > 0 && (
        <div className="px-5 py-3 flex items-center gap-2 overflow-x-auto border-b border-background-200/70">
          {reactions.map((r) => (
            <button
              key={r.type}
              onClick={() => onReaction(r.type)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-background-100 hover:bg-background-200 rounded-full cursor-pointer transition-colors whitespace-nowrap"
            >
              <span className="text-base">{r.type}</span>
              <span className="text-xs text-foreground-600 font-semibold">{r.count}</span>
            </button>
          ))}
        </div>
      )}

      <div className="flex-1 px-5 py-4 space-y-4 overflow-y-auto max-h-[420px]">
        {allMessages.map((c) => (
          <div key={c.id} className="flex gap-3 group">
            <div className="w-9 h-9 rounded-full overflow-hidden bg-foreground-100 flex-shrink-0">
              <img src={c.avatarUrl} alt={c.userName} className="w-full h-full object-cover" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="bg-background-100 rounded-2xl rounded-tl-md px-3.5 py-2.5">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-semibold text-foreground-800">{c.userName}</p>
                  <button
                    onClick={() => onReport(c.id)}
                    className="opacity-0 group-hover:opacity-100 text-foreground-300 hover:text-red-400 transition-all cursor-pointer"
                  >
                    <i className="ri-flag-line text-[10px]"></i>
                  </button>
                </div>
                <p className="text-sm text-foreground-700 mt-0.5 leading-relaxed">{c.text}</p>
              </div>
              <p className="text-[10px] text-foreground-400 mt-0.5 ml-1.5">{c.time}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="px-5 py-4 border-t border-background-200/70">
        <div className="flex items-center gap-3 bg-background-100 rounded-full pl-4 pr-2 py-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
            placeholder="Escribe un mensaje..."
            maxLength={500}
            className="flex-1 bg-transparent text-sm text-foreground-800 placeholder:text-foreground-300 outline-none"
          />
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => onReaction('❤️')}
              className="w-8 h-8 rounded-full bg-background-200/70 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
            >
              <span className="text-sm">❤️</span>
            </button>
            <button
              onClick={handleSend}
              disabled={!input.trim()}
              className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center cursor-pointer hover:bg-primary-600 transition-colors disabled:opacity-40"
            >
              <i className="ri-send-plane-fill text-background-50 text-xs"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function LiveHero({ live, onShare, onFollow, followed, station }: {
  live: RadioLive;
  onShare: () => void;
  onFollow: () => void;
  followed: boolean;
  station: RadioStation;
}) {
  return (
    <div className="bg-foreground-950 rounded-2xl overflow-hidden">
      <div className="flex flex-col lg:flex-row">
        <div className="lg:w-[60%]">
          <div className="relative aspect-video lg:aspect-[16/10] overflow-hidden">
            <img src={live.imageUrl} alt={live.title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/30 to-foreground-950/10"></div>

            <div className="absolute top-4 left-4">
              <StatusBadge status={live.status} />
            </div>

            <div className="absolute top-4 right-4 flex items-center gap-2">
              <button
                onClick={onShare}
                className="w-9 h-9 rounded-full bg-background-50/20 backdrop-blur-sm flex items-center justify-center cursor-pointer hover:bg-background-50/30 transition-colors"
              >
                <i className="ri-share-forward-line text-background-50 text-sm"></i>
              </button>
              <button
                onClick={onFollow}
                className={`px-3 py-1.5 rounded-full text-xs font-bold cursor-pointer whitespace-nowrap transition-all ${
                  followed
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-50/20 backdrop-blur-sm text-background-50 hover:bg-background-50/30'
                }`}
              >
                <i className={`${followed ? 'ri-user-follow-fill' : 'ri-user-follow-line'} mr-1 text-xs`}></i>
                {followed ? 'Siguiendo' : 'Seguir'}
              </button>
            </div>

            <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
              <h2 className="text-background-50 text-lg md:text-xl font-bold mb-2">{live.title}</h2>
              <p className="text-background-50/80 text-sm mb-3 line-clamp-2 max-w-2xl">{live.description}</p>
              <div className="flex flex-wrap items-center gap-3 md:gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full overflow-hidden bg-foreground-800">
                    <img src={live.imageUrl} alt={live.hostName} className="w-full h-full object-cover" />
                  </div>
                  <span className="text-background-50 text-sm font-medium">{live.hostName}</span>
                </div>
                <div className="flex items-center gap-1.5 text-background-50/70 text-sm">
                  <i className="ri-eye-line"></i>
                  <span><FormattedCount count={live.viewers} /> viendo</span>
                </div>
                <div className="flex items-center gap-1.5 text-background-50/70 text-sm">
                  <i className="ri-time-line"></i>
                  <span>{live.duration}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="hidden lg:block lg:w-[40%] border-l border-foreground-800">
          <LiveChat
            live={live}
            onSendMessage={() => {}}
            messages={[]}
            onReaction={() => {}}
            onReport={() => {}}
          />
        </div>
      </div>
    </div>
  );
}

function LiveCard({ live, onClick }: { live: RadioLive; onClick: () => void }) {
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  return (
    <div
      onClick={onClick}
      className="group bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden hover:border-background-300/60 transition-all cursor-pointer"
    >
      <div className="relative aspect-video overflow-hidden">
        <img
          src={live.imageUrl}
          alt={live.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        {live.status === 'finalizado' && (
          <div className="absolute inset-0 bg-foreground-950/20"></div>
        )}
        <div className="absolute top-3 left-3">
          <StatusBadge status={live.status} />
        </div>
        {live.status === 'en_vivo' && (
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="w-14 h-14 rounded-full bg-accent-500/90 flex items-center justify-center cursor-pointer">
              <i className="ri-play-fill text-background-50 text-2xl ml-0.5"></i>
            </div>
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <h4 className="font-semibold text-foreground-950 text-sm mb-1.5 line-clamp-2 group-hover:text-primary-600 transition-colors flex-1">
            {live.title}
          </h4>
          <ReportButton
            contentType="live"
            contentLabel={live.title}
            contentPreview={`${live.title} — ${live.hostName}`}
            variant="icon"
          />
        </div>
        <p className="text-xs text-foreground-500 mb-3">{live.hostName}</p>
        <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-400">
          {live.scheduledDate ? (
            <>
              <div className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                <span>{formatDate(live.scheduledDate)}</span>
              </div>
              <div className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                <span>{live.scheduledTime}</span>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                <span>{formatDate(live.date)}</span>
              </div>
              <div className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                <span>{live.duration}</span>
              </div>
            </>
          )}
        </div>
        {live.viewers > 0 && (
          <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-background-200/70">
            <i className="ri-eye-line text-xs text-foreground-400"></i>
            <span className="text-xs text-foreground-500 font-medium"><FormattedCount count={live.viewers} /> espectadores</span>
          </div>
        )}
      </div>
    </div>
  );
}

function ProgramadoHero({ live, station, onShare, onFollow, followed }: {
  live: RadioLive;
  station: RadioStation;
  onShare: () => void;
  onFollow: () => void;
  followed: boolean;
}) {
  const scheduledDate = live.scheduledDate
    ? new Date(live.scheduledDate + 'T' + (live.scheduledTime || '00:00'))
    : null;

  const formatFull = (d: Date) =>
    d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="bg-foreground-950 rounded-2xl overflow-hidden">
      <div className="relative aspect-video lg:aspect-[16/10] overflow-hidden">
        <img src={live.imageUrl} alt={live.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/30 to-foreground-950/10"></div>
        <div className="absolute top-4 left-4">
          <StatusBadge status={live.status} />
        </div>
        <div className="absolute top-4 right-4 flex items-center gap-2">
          <button
            onClick={onShare}
            className="w-9 h-9 rounded-full bg-background-50/20 backdrop-blur-sm flex items-center justify-center cursor-pointer hover:bg-background-50/30 transition-colors"
          >
            <i className="ri-share-forward-line text-background-50 text-sm"></i>
          </button>
          <button
            onClick={onFollow}
            className={`px-3 py-1.5 rounded-full text-xs font-bold cursor-pointer whitespace-nowrap transition-all ${
              followed
                ? 'bg-primary-500 text-background-50'
                : 'bg-background-50/20 backdrop-blur-sm text-background-50 hover:bg-background-50/30'
            }`}
          >
            <i className={`${followed ? 'ri-user-follow-fill' : 'ri-user-follow-line'} mr-1 text-xs`}></i>
            {followed ? 'Siguiendo' : 'Seguir'}
          </button>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
          <h2 className="text-background-50 text-lg md:text-xl font-bold mb-2">{live.title}</h2>
          <p className="text-background-50/80 text-sm mb-3 line-clamp-2 max-w-2xl">{live.description}</p>
          <div className="flex flex-wrap items-center gap-3 md:gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full overflow-hidden bg-foreground-800">
                <img src={live.imageUrl} alt={live.hostName} className="w-full h-full object-cover" />
              </div>
              <span className="text-background-50 text-sm font-medium">{live.hostName}</span>
            </div>
            {scheduledDate && (
              <div className="flex items-center gap-1.5 text-background-50/70 text-sm">
                <i className="ri-calendar-line"></i>
                <span>{formatFull(scheduledDate)}</span>
              </div>
            )}
            {live.scheduledTime && (
              <div className="flex items-center gap-1.5 text-background-50/70 text-sm">
                <i className="ri-time-line"></i>
                <span>{live.scheduledTime} hs</span>
              </div>
            )}
          </div>
          <button
            onClick={() => SwalAlert.toast('Te notificaremos cuando empiece el Live', 'info')}
            className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-accent-500 text-background-50 text-xs font-bold hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-notification-3-line text-sm"></i>
            Notificarme cuando empiece
          </button>
        </div>
      </div>
    </div>
  );
}

function FinalizadoHero({ live, station, onShare, onFollow, followed }: {
  live: RadioLive;
  station: RadioStation;
  onShare: () => void;
  onFollow: () => void;
  followed: boolean;
}) {
  return (
    <div className="bg-foreground-950 rounded-2xl overflow-hidden">
      <div className="relative aspect-video lg:aspect-[16/10] overflow-hidden">
        <img src={live.imageUrl} alt={live.title} className="w-full h-full object-cover opacity-80" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/40 to-foreground-950/20"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-foreground-950/50 backdrop-blur-sm flex items-center justify-center mx-auto mb-3">
              <i className="ri-check-double-line text-background-50/80 text-2xl"></i>
            </div>
            <p className="text-background-50/60 text-sm font-medium">Transmisión finalizada</p>
          </div>
        </div>
        <div className="absolute top-4 left-4">
          <StatusBadge status="finalizado" />
        </div>
        <div className="absolute top-4 right-4 flex items-center gap-2">
          <button
            onClick={onShare}
            className="w-9 h-9 rounded-full bg-background-50/20 backdrop-blur-sm flex items-center justify-center cursor-pointer hover:bg-background-50/30 transition-colors"
          >
            <i className="ri-share-forward-line text-background-50 text-sm"></i>
          </button>
          <button
            onClick={onFollow}
            className={`px-3 py-1.5 rounded-full text-xs font-bold cursor-pointer whitespace-nowrap transition-all ${
              followed
                ? 'bg-primary-500 text-background-50'
                : 'bg-background-50/20 backdrop-blur-sm text-background-50 hover:bg-background-50/30'
            }`}
          >
            <i className={`${followed ? 'ri-user-follow-fill' : 'ri-user-follow-line'} mr-1 text-xs`}></i>
            {followed ? 'Siguiendo' : 'Seguir'}
          </button>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
          <h2 className="text-background-50 text-lg md:text-xl font-bold mb-2">{live.title}</h2>
          <p className="text-background-50/80 text-sm mb-3 line-clamp-2 max-w-2xl">{live.description}</p>
          <div className="flex flex-wrap items-center gap-3 md:gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full overflow-hidden bg-foreground-800">
                <img src={live.imageUrl} alt={live.hostName} className="w-full h-full object-cover" />
              </div>
              <span className="text-background-50 text-sm font-medium">{live.hostName}</span>
            </div>
            <div className="flex items-center gap-1.5 text-background-50/70 text-sm">
              <i className="ri-eye-line"></i>
              <span><FormattedCount count={live.viewers} /> vieron</span>
            </div>
            <div className="flex items-center gap-1.5 text-background-50/70 text-sm">
              <i className="ri-time-line"></i>
              <span>{live.duration}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DesktopBanner({ station, onShare, onFollow, followed }: {
  station: RadioStation;
  onShare: () => void;
  onFollow: () => void;
  followed: boolean;
}) {
  return (
    <section className="relative">
      <div className="relative h-[220px] md:h-[280px] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(https://readdy.ai/api/search-image?query=Abstract%20sound%20wave%20visualization%20with%20smooth%20flowing%20lines%2C%20dark%20deep%20navy%20background%20with%20soft%20golden%20and%20warm%20amber%20frequency%20waves%2C%20minimalist%20modern%20audio%20spectrum%20design%2C%20elegant%20broadcast%20atmosphere%2C%20subtle%20gradient%20lighting%2C%20radio%20station%20hero%20backdrop%2C%20cinematic%20wide%20composition%2C%20no%20text%20no%20logos&width=1600&height=600&seq=radio-live-hero-${station.id}&orientation=landscape)`,
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-900/60 via-foreground-900/40 to-foreground-900/85"></div>
      </div>
      <div className="relative px-4 md:px-6 -mt-16 md:-mt-20">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end gap-3 md:gap-5">
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl overflow-hidden border-4 border-background-50 bg-foreground-800 shrink-0 shadow-lg">
              <img src={station.logoUrl} alt={station.name} className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <h1 className="text-xl md:text-2xl font-bold text-background-50">{station.name}</h1>
                <span className="text-background-50/60 text-xs font-medium">{station.frequency}</span>
              </div>
              <div className="flex items-center gap-3 text-background-50/60 text-xs">
                <span>{station.city}, {station.state}</span>
                <span>·</span>
                <span>{station.category}</span>
                {station.isLive && (
                  <>
                    <span>·</span>
                    <span className="flex items-center gap-1">
                      <span className="w-1.5 h-1.5 bg-accent-500 rounded-full animate-pulse"></span>
                      <span className="text-accent-400 font-bold uppercase">EN VIVO</span>
                    </span>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Link
                to={`/radio/${station.slug || station.frequency.replace('.', '-')}`}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-background-50/15 text-background-50 hover:bg-background-50/25 text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line text-sm"></i>
                Volver a la radio
              </Link>
              <button
                onClick={onFollow}
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold cursor-pointer whitespace-nowrap transition-all ${
                  followed
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-50/15 text-background-50 hover:bg-background-50/25'
                }`}
              >
                <i className={`${followed ? 'ri-user-follow-fill' : 'ri-user-follow-line'} text-sm`}></i>
                {followed ? 'Siguiendo' : 'Seguir'}
              </button>
              <button
                onClick={onShare}
                className="w-9 h-9 rounded-full bg-background-50/15 text-background-50 hover:bg-background-50/25 flex items-center justify-center cursor-pointer transition-all"
              >
                <i className="ri-share-forward-line text-sm"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function RadioLivePage() {
  const { frequency } = useParams<{ frequency: string }>();
  const [pageState, setPageState] = useState<PageState>('loading');
  const [followed, setFollowed] = useState(false);
  const [chatMessages, setChatMessages] = useState<LiveComment[]>([]);
  const [selectedLive, setSelectedLive] = useState<RadioLive | null>(null);

  const decodedId = frequency || '';

  const station = radioStations.find((s) => {
    const freqHyphen = s.frequency.replace('.', '-');
    return s.slug === decodedId || s.frequency === decodedId || freqHyphen === decodedId;
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [frequency]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPageState('ready');
    }, 600);
    return () => clearTimeout(timer);
  }, []);

  const handleRetry = useCallback(() => {
    setPageState('loading');
    setTimeout(() => setPageState('ready'), 600);
  }, []);

  const handleShare = useCallback(() => {
    if (navigator.share) {
      navigator.share({
        title: `Live - ${station?.name || 'RadioFrecuenciaWeb'}`,
        text: `Mirá el live en vivo de ${station?.name || 'esta radio'}`,
        url: window.location.href,
      }).catch(() => {});
    } else {
      SwalAlert.toast('Enlace copiado al portapapeles', 'info');
    }
  }, [station]);

  const handleFollow = useCallback(() => {
    setFollowed(!followed);
    SwalAlert.toast(followed ? 'Dejaste de seguir la radio' : '¡Ahora seguís esta radio!', followed ? 'info' : 'success');
  }, [followed]);

  const handleSendMessage = useCallback((text: string) => {
    const newMsg: LiveComment = {
      id: Date.now(),
      userName: 'Vos',
      avatarUrl: 'https://readdy.ai/api/search-image?query=Default%20user%20profile%20avatar%2C%20simple%20friendly%20face%2C%20minimal%20style%2C%20clean%20background%2C%20casual%20portrait%20photography%2C%20warm%20friendly%20expression&width=80&height=80&seq=avatar-you&orientation=squarish',
      text,
      time: 'Ahora',
    };
    setChatMessages((prev) => [...prev, newMsg]);
    SwalAlert.toast('Mensaje enviado', 'success');
  }, []);

  const handleReaction = useCallback((type: string) => {
    SwalAlert.toast(`Reaccionaste con ${type}`, 'success');
  }, []);

  const handleReport = useCallback((commentId: number) => {
    SwalAlert.confirm('Reportar mensaje', '¿Estás seguro de reportar este mensaje? Un moderador lo revisará.')
      .then((result) => {
        if (result.isConfirmed) {
          SwalAlert.toast('Mensaje reportado. Gracias por ayudarnos.', 'success');
        }
      });
  }, []);

  const handleEnterLive = useCallback((live: RadioLive) => {
    setSelectedLive(live);
    SwalAlert.toast(`Entrando a: ${live.title}`, 'info');
  }, []);

  // Station not found
  if (!station) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center px-4">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-foreground-100 flex items-center justify-center">
              <i className="ri-signal-wifi-error-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-2xl font-bold text-foreground-950 mb-2">Frecuencia no encontrada</h1>
            <p className="text-foreground-500 mb-6">La estación que buscás no está disponible.</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-xl transition-all cursor-pointer text-sm"
            >
              <i className="ri-arrow-left-line"></i>
              Volver al Dial
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const lives = getStationLives(station.id);
  const enVivo = lives.find((l) => l.status === 'en_vivo') || null;
  const programados = lives.filter((l) => l.status === 'programado');
  const finalizados = lives.filter((l) => l.status === 'finalizado');

  return (
    <div className="min-h-screen bg-background-100/50 flex flex-col">
      <Header />
      <DesktopBanner station={station} onShare={handleShare} onFollow={handleFollow} followed={followed} />

      <main className="flex-1">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-6 md:py-8 space-y-8">
          {/* Loading */}
          {pageState === 'loading' && (
            <div className="animate-pulse space-y-6">
              <div className="bg-background-200 rounded-2xl h-[300px] md:h-[400px]"></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-background-200 rounded-2xl h-64"></div>
                ))}
              </div>
            </div>
          )}

          {/* Error */}
          {pageState === 'error' && (
            <div className="bg-background-50 border border-background-200 rounded-2xl p-10 text-center">
              <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
                <i className="ri-error-warning-line text-red-500 text-2xl"></i>
              </div>
              <h3 className="text-foreground-950 font-bold text-sm mb-1">Error al cargar el Live</h3>
              <p className="text-foreground-400 text-xs mb-4">No se pudieron obtener las transmisiones. Intentá de nuevo.</p>
              <button
                onClick={handleRetry}
                className="px-5 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line mr-1.5"></i>
                Reintentar
              </button>
            </div>
          )}

          {/* Empty */}
          {pageState === 'empty' && (
            <div className="bg-background-50 border border-background-200/70 rounded-2xl p-10 text-center">
              <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-foreground-100 flex items-center justify-center">
                <i className="ri-live-line text-3xl text-foreground-400"></i>
              </div>
              <h3 className="text-lg font-semibold text-foreground-950 mb-2">Sin transmisiones disponibles</h3>
              <p className="text-foreground-500 text-sm max-w-md mx-auto mb-6">
                Esta estación no tiene transmisiones en vivo programadas en este momento.
              </p>
              <Link
                to={`/radio/${frequency}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-secondary-500 text-background-50 text-sm font-bold hover:bg-secondary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                Volver a la radio
              </Link>
            </div>
          )}

          {/* Ready */}
          {pageState === 'ready' && lives.length === 0 && (
            <div className="bg-background-50 border border-background-200/70 rounded-2xl p-10 text-center">
              <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-foreground-100 flex items-center justify-center">
                <i className="ri-live-line text-3xl text-foreground-400"></i>
              </div>
              <h3 className="text-lg font-semibold text-foreground-950 mb-2">Sin transmisiones disponibles</h3>
              <p className="text-foreground-500 text-sm max-w-md mx-auto mb-6">
                Esta estación no tiene transmisiones en vivo programadas en este momento. Vuelve pronto para nuevas emisiones.
              </p>
              <Link
                to={`/radio/${frequency}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-secondary-500 text-background-50 text-sm font-bold hover:bg-secondary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                Volver a la radio
              </Link>
            </div>
          )}

          {pageState === 'ready' && lives.length > 0 && (
            <>
              {/* Current / Featured live */}
              {enVivo && (
                <div>
                  <div className="flex items-center gap-2.5 mb-4">
                    <div className="w-9 h-9 rounded-full bg-accent-100 flex items-center justify-center">
                      <i className="ri-live-line text-accent-600 text-lg"></i>
                    </div>
                    <h3 className="text-lg font-bold text-foreground-950">Transmisión en Vivo</h3>
                  </div>
                  <LiveHero
                    live={enVivo}
                    station={station}
                    onShare={handleShare}
                    onFollow={handleFollow}
                    followed={followed}
                  />
                  {/* Mobile chat */}
                  <div className="lg:hidden mt-4">
                    <LiveChat
                      live={enVivo}
                      onSendMessage={handleSendMessage}
                      messages={chatMessages}
                      onReaction={handleReaction}
                      onReport={handleReport}
                    />
                  </div>
                </div>
              )}

              {/* Primer programado como destacado cuando no hay en_vivo */}
              {!enVivo && programados.length > 0 && (
                <div>
                  <div className="flex items-center gap-2.5 mb-4">
                    <div className="w-9 h-9 rounded-full bg-secondary-100 flex items-center justify-center">
                      <i className="ri-calendar-todo-line text-secondary-600 text-lg"></i>
                    </div>
                    <h3 className="text-lg font-bold text-foreground-950">Próxima Transmisión</h3>
                  </div>
                  <ProgramadoHero
                    live={programados[0]}
                    station={station}
                    onShare={handleShare}
                    onFollow={handleFollow}
                    followed={followed}
                  />
                </div>
              )}

              {/* Si solo hay finalizados, mostrar el más reciente */}
              {!enVivo && programados.length === 0 && finalizados.length > 0 && (
                <div>
                  <div className="flex items-center gap-2.5 mb-4">
                    <div className="w-9 h-9 rounded-full bg-foreground-100 flex items-center justify-center">
                      <i className="ri-history-line text-foreground-500 text-lg"></i>
                    </div>
                    <h3 className="text-lg font-bold text-foreground-950">Última Transmisión</h3>
                  </div>
                  <FinalizadoHero
                    live={finalizados[0]}
                    station={station}
                    onShare={handleShare}
                    onFollow={handleFollow}
                    followed={followed}
                  />
                </div>
              )}

              {/* Host & Current program info */}
              {enVivo && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-background-50 border border-background-200/70 rounded-2xl p-5 flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl overflow-hidden bg-foreground-100 shrink-0">
                      <img src={enVivo.imageUrl} alt={enVivo.hostName} className="w-full h-full object-cover" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-foreground-400 text-[10px] font-medium mb-0.5">Locutor</p>
                      <p className="text-foreground-950 text-sm font-bold">{enVivo.hostName}</p>
                      <p className="text-foreground-400 text-[10px] mt-0.5">Al aire ahora</p>
                    </div>
                  </div>
                  <div className="bg-background-50 border border-background-200/70 rounded-2xl p-5 flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-accent-100 flex items-center justify-center shrink-0">
                      <i className="ri-broadcast-line text-accent-600 text-xl"></i>
                    </div>
                    <div className="min-w-0">
                      <p className="text-foreground-400 text-[10px] font-medium mb-0.5">Programa Actual</p>
                      <p className="text-foreground-950 text-sm font-bold">{enVivo.title}</p>
                      <p className="text-foreground-400 text-[10px] mt-0.5">{enVivo.duration}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Programados (excluding the featured one) */}
              {programados.length > (enVivo ? 0 : 1) && (
                <div>
                  <div className="flex items-center gap-2.5 mb-4">
                    <div className="w-9 h-9 rounded-full bg-secondary-100 flex items-center justify-center">
                      <i className="ri-calendar-todo-line text-secondary-600 text-lg"></i>
                    </div>
                    <h3 className="text-lg font-bold text-foreground-950">Próximas Transmisiones</h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {programados.slice(enVivo ? 0 : 1).map((live) => (
                      <LiveCard key={live.id} live={live} onClick={() => handleEnterLive(live)} />
                    ))}
                  </div>
                </div>
              )}

              {/* Programados when en_vivo exists */}
              {enVivo && programados.length > 0 && (
                <div>
                  <div className="flex items-center gap-2.5 mb-4">
                    <div className="w-9 h-9 rounded-full bg-secondary-100 flex items-center justify-center">
                      <i className="ri-calendar-todo-line text-secondary-600 text-lg"></i>
                    </div>
                    <h3 className="text-lg font-bold text-foreground-950">Próximas Transmisiones</h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {programados.map((live) => (
                      <LiveCard key={live.id} live={live} onClick={() => handleEnterLive(live)} />
                    ))}
                  </div>
                </div>
              )}

              {/* Historial (finalizados except the featured one) */}
              {finalizados.length > 0 && (
                <div>
                  <div className="flex items-center gap-2.5 mb-4">
                    <div className="w-9 h-9 rounded-full bg-foreground-100 flex items-center justify-center">
                      <i className="ri-history-line text-foreground-500 text-lg"></i>
                    </div>
                    <h3 className="text-lg font-bold text-foreground-950">Historial de Lives</h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {finalizados.map((live) => (
                      <LiveCard key={live.id} live={live} onClick={() => handleEnterLive(live)} />
                    ))}
                  </div>
                </div>
              )}

              {/* Preparado para futuras transmisiones */}
              <div className="bg-background-50 border border-background-200/70 rounded-2xl p-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
                  <div>
                    <p className="text-sm font-semibold text-foreground-950">¿Querés transmitir en vivo?</p>
                    <p className="text-xs text-foreground-500 mt-0.5">
                      Próximamente podrás iniciar transmisiones desde la cabina o tu celular.
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="flex items-center gap-1.5 px-4 py-2 bg-foreground-100 rounded-full text-xs text-foreground-500 font-medium">
                      <i className="ri-broadcast-line"></i>
                      Desde Cabina
                    </span>
                    <span className="flex items-center gap-1.5 px-4 py-2 bg-foreground-100 rounded-full text-xs text-foreground-500 font-medium">
                      <i className="ri-smartphone-line"></i>
                      Desde Celular
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      <div className="hidden md:block">
        <Footer />
      </div>
      <div className="md:hidden h-16"></div>
    </div>
  );
}